/**
 * The commits the running build was made from (Settings → Diagnostics). The build reads the
 * recent history as plain text, one commit per line, newest first, fields separated by U+001F:
 * `<full hash>␟<committer date, ISO 8601>␟<subject>`. `vite.config.ts` injects that text as
 * `__WIZTIME_BUILD_INFO__`; it is empty when the build had neither a `build-info.txt` nor a git
 * checkout (for example a plain `docker compose build` of a source archive).
 */

export interface BuildCommit {
  readonly hash: string;
  /** Committer date, ISO 8601 with the committer's UTC offset. */
  readonly date: string;
  readonly subject: string;
}

export const BUILD_INFO_FIELD_SEPARATOR = '\u001f';
/** How many commits the build records. */
export const BUILD_INFO_COMMIT_COUNT = 10;
/** The `git log --format` that produces the text `parseBuildInfo` reads. */
export const BUILD_INFO_GIT_LOG_FORMAT = '%H%x1f%cI%x1f%s';

export function parseBuildInfo(text: string): BuildCommit[] {
  return text.split('\n').flatMap((line) => {
    const [hash, date, ...subject] = line.split(BUILD_INFO_FIELD_SEPARATOR);
    if (hash === undefined || date === undefined || !/^[0-9a-f]{7,64}$/.test(hash)) return [];
    return [{ hash, date, subject: subject.join(BUILD_INFO_FIELD_SEPARATOR).trim() }];
  });
}

declare const __WIZTIME_BUILD_INFO__: string | undefined;

/** Newest first; empty when the build did not record its commits. */
export const BUILD_COMMITS: readonly BuildCommit[] = parseBuildInfo(
  typeof __WIZTIME_BUILD_INFO__ === 'string' ? __WIZTIME_BUILD_INFO__ : '',
);
